/**
 * features/burs/ITW10Reconciliation.tsx
 * ------------------------------------------------------------------
 * BURS — ITW-10 annual PAYE reconciliation. Pick a tax year; shows
 * the month-by-month PAYE totals reconciling to the annual figure,
 * plus a per-employee breakdown, with PDF and CSV. Due within 31
 * days of the tax year end (30 June). Renders embedded in the hub.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useHrData } from '@/features/hr/useHrData';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { fmtPula } from '@/lib/hrPayroll';
import {
  buildAnnualReconciliation,
  resolveEmployerDetails,
  taxYearEndYearOf,
  toCsv,
  downloadCsv,
} from '@/lib/bursForms';
import { buildItw10Pdf } from '@/lib/bursPdf';

export default function ITW10Reconciliation() {
  const data = useHrData();
  const { userData } = useAuth();
  const defaultEnd = taxYearEndYearOf({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [endYear, setEndYear] = useState(defaultEnd);
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => { loadLogoDataUrl().then(setLogo); }, []);

  const recon = useMemo(() => buildAnnualReconciliation(data.payslips, endYear), [data.payslips, endYear]);
  const emp = resolveEmployerDetails(data.payrollSettings);

  function downloadPdf() {
    buildItw10Pdf(recon, emp, { logoDataUrl: logo, preparedBy: userData?.displayName }).save(`ITW-10_${endYear}.pdf`);
  }
  function exportCsv() {
    const head = ['Section', 'Ref', 'Label', 'Employees/Months', 'Gross', 'PAYE', 'Lump-sum Tax', 'Total Tax'];
    const monthRows = recon.months.map((m) => ['Month', `${m.year}-${String(m.month).padStart(2, '0')}`, m.monthLabel, String(m.employees), m.gross.toFixed(2), m.paye.toFixed(2), m.gratuityTax.toFixed(2), m.totalTax.toFixed(2)]);
    const empRows = recon.byEmployee.map((r) => ['Employee', r.employeeNo, r.name, String(r.monthsPaid), r.gross.toFixed(2), r.paye.toFixed(2), r.gratuityTax.toFixed(2), r.totalTax.toFixed(2)]);
    const totalRow = ['Total', '', `Year ${recon.taxYear.shortLabel}`, String(recon.totals.employees), recon.totals.gross.toFixed(2), recon.totals.paye.toFixed(2), recon.totals.gratuityTax.toFixed(2), recon.totals.totalTax.toFixed(2)];
    downloadCsv(`ITW-10_${endYear}.csv`, toCsv([head, ...monthRows, ...empRows, totalRow]));
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  const yearOptions = [defaultEnd + 1, defaultEnd, defaultEnd - 1, defaultEnd - 2];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500 dark:text-gray-400">{recon.taxYear.label} · annual PAYE reconciliation · due within 31 days of 30 June</p>
        <div className="flex items-center gap-2">
          <select value={endYear} onChange={(e) => setEndYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
            {yearOptions.map((y) => <option key={y} value={y}>{`FY ending 30 Jun ${y}`}</option>)}
          </select>
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl">
            <Download size={15} /> CSV
          </button>
          <button onClick={downloadPdf} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
            <FileText size={15} /> Download ITW-10 PDF
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <StatChip tone="emerald" value={`P${fmtPula(recon.totals.totalTax)}`} label="total PAYE for year" />
        <StatChip tone="slate" value={String(recon.totals.employees)} label="employees" />
        <StatChip tone="blue" value={`P${fmtPula(recon.totals.gross)}`} label="gross emoluments" />
      </div>

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Monthly summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 px-4 font-medium">Month</th>
                <th className="py-2 px-4 font-medium text-right">Employees</th>
                <th className="py-2 px-4 font-medium text-right">Gross</th>
                <th className="py-2 px-4 font-medium text-right">PAYE</th>
                <th className="py-2 px-4 font-medium text-right">Total tax</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {recon.months.map((m) => (
                <tr key={`${m.year}-${m.month}`} className={m.employees === 0 ? 'opacity-50' : ''}>
                  <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{m.monthLabel}</td>
                  <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">{m.employees || '—'}</td>
                  <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(m.gross)}</td>
                  <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(m.paye)}</td>
                  <td className="py-2 px-4 text-right font-medium text-gray-900 dark:text-white">{fmtPula(m.totalTax)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                <td className="py-2.5 px-4">Year total</td>
                <td className="py-2.5 px-4 text-right">{recon.totals.employees}</td>
                <td className="py-2.5 px-4 text-right">{fmtPula(recon.totals.gross)}</td>
                <td className="py-2.5 px-4 text-right">{fmtPula(recon.totals.paye)}</td>
                <td className="py-2.5 px-4 text-right">{fmtPula(recon.totals.totalTax)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </SoftCard>

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Per-employee breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 px-4 font-medium">Employee No</th>
                <th className="py-2 px-4 font-medium">Name</th>
                <th className="py-2 px-4 font-medium text-right">Months</th>
                <th className="py-2 px-4 font-medium text-right">Gross</th>
                <th className="py-2 px-4 font-medium text-right">PAYE</th>
                <th className="py-2 px-4 font-medium text-right">Total tax</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {recon.byEmployee.map((r) => (
                <tr key={r.staffId}>
                  <td className="py-2 px-4 text-gray-500 dark:text-gray-400">#{r.employeeNo}</td>
                  <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{r.name}</td>
                  <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">{r.monthsPaid}</td>
                  <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(r.gross)}</td>
                  <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-100">{fmtPula(r.paye)}</td>
                  <td className="py-2 px-4 text-right font-medium text-gray-900 dark:text-white">{fmtPula(r.totalTax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>
    </div>
  );
}
